import type { ReactNode } from 'react';
import { Tooltip } from '@/shared/components/Tooltip';
import FilePDFIcon from '@/assets/svg/FilePDF.svg';
import FilePNGIcon from '@/assets/svg/FilePNG.svg';
import FileJPGIcon from '@/assets/svg/FileJPG.svg';
import FileJPEGIcon from '@/assets/svg/FileJPEG.svg';
import type {
  UserProfile,
  ProfileDoc,
  ProfileSignature,
} from '../../api/viewProfileApiService';

// ============================================================================
// Helpers
// ============================================================================

function fmt(value: string | null | undefined): string {
  return value?.trim() || '—';
}

function formatPhone(
  countryCode: string | null | undefined,
  phone: string | null | undefined
): string {
  if (!phone) {
    return '—';
  }
  const trimmed = countryCode?.trim() ?? '';
  const code = trimmed.startsWith('+') ? trimmed : '+48';
  return `${code} ${phone}`;
}

function formatCurrency(currency: string | null | undefined): string {
  if (!currency) {
    return '—';
  }
  if (currency.toUpperCase() === 'PLN') {
    return 'zł - Złoty polski (PLN)';
  }
  return currency;
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) {
    return '—';
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

function formatDate(date: string | null | undefined): string {
  if (!date) {
    return '—';
  }
  try {
    return new Date(date).toLocaleDateString('en-CA'); // YYYY/MM/DD
  } catch {
    return date;
  }
}

function openDocInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function getDocIcon(filename: string): ReactNode {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'pdf':
      return <FilePDFIcon />;
    case 'png':
      return <FilePNGIcon />;
    case 'jpeg':
      return <FileJPEGIcon />;
    case 'jpg':
      return <FileJPGIcon />;
    default:
      return <FilePDFIcon />;
  }
}

function getPaymentStatusLabel(status: string | null | undefined): string {
  if (status === 'paid') {
    return 'Opłacono';
  }
  if (status === 'unpaid') {
    return 'Oczekuje';
  }
  return '—';
}

// ============================================================================
// InfoField
// ============================================================================

interface InfoFieldProps {
  label: string;
  value: string;
  testId?: string;
}

const InfoField = ({ label, value, testId }: InfoFieldProps) => (
  <div className="flex min-w-0 flex-col gap-2">
    <h2
      className="text-bodySmall font-normal text-on-surface-variant"
      data-testid={testId ?? label}
    >
      {label}
    </h2>
    <p
      className="min-w-0 text-bodyLarge font-normal text-neutral-10"
      style={{ overflowWrap: 'anywhere' }}
      data-testid={testId ? `${testId}-value` : `${label}-value`}
    >
      {value}
    </p>
  </div>
);

// ============================================================================
// SignatureSection
// ============================================================================

interface SignatureSectionProps {
  signature: ProfileSignature | null;
}

const SignatureSection = ({ signature }: SignatureSectionProps) => (
  <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
    <p
      className="text-[18px] font-medium text-on-surface-variant"
      data-testid="profile-signature-title"
    >
      Podpis
    </p>
    {signature ? (
      <div className="flex items-center gap-3 p-2">
        <button
          type="button"
          onClick={() => signature.url && openDocInNewTab(signature.url)}
          className="shrink-0 p-0 border-0 bg-transparent cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 rounded"
          style={{ width: '40px', height: '40px' }}
          aria-label={`Otwórz ${signature.filename}`}
          data-testid="profile-signature-icon"
        >
          <FilePDFIcon />
        </button>
        <div className="flex flex-col min-w-0 flex-1">
          <p
            className="text-[16px] leading-6 tracking-[0.5px] text-[#2A4455] font-normal truncate"
            data-testid="profile-signature-filename"
          >
            {signature.filename}
          </p>
          <p
            className="text-[12px] leading-4 tracking-[0.4px] text-[#878B8E] font-normal"
            data-testid="profile-signature-filesize"
          >
            {formatFileSize(signature.fileSize)}
          </p>
        </div>
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center py-4">
        <p
          className="text-bodyMedium text-on-surface-variant"
          data-testid="profile-signature-empty"
        >
          No signature uploaded.
        </p>
      </div>
    )}
  </div>
);

// ============================================================================
// Component
// ============================================================================

interface ProfileBasicInfoProps {
  profileData: UserProfile | undefined;
}

const ProfileBasicInfo = ({ profileData }: ProfileBasicInfoProps) => {
  if (!profileData) {
    return null;
  }

  const isClient = profileData.role === 'CLIENT';
  const isTechnician = profileData.role === 'TECHNICIAN';
  const docs: ProfileDoc[] = profileData.contractDetails?.docs ?? [];
  const hasDocs = docs.length > 0;
  const nip = profileData.nip?.trim() || '—';
  const subscription = profileData.subscriptionDetails;

  return (
    <div className="flex flex-col gap-6">
      {/* Basic Details */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          data-testid="profile-basic-details-title"
        >
          Podstawowe dane
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          {isClient && (
            <InfoField
              label="ID klienta"
              value={fmt(profileData.clientId)}
              testId="profile-id"
            />
          )}
          <InfoField
            label="Adres e-mail"
            value={fmt(profileData.email)}
            testId="profile-email"
          />
          <InfoField
            label="Numer telefonu"
            value={formatPhone(
              profileData.countryCode,
              profileData.phoneNumber
            )}
            testId="profile-phone"
          />
          {isClient && (
            <InfoField
              label="Preferowana waluta"
              value={formatCurrency(profileData.preferredCurrency)}
              testId="profile-currency"
            />
          )}
        </div>
      </div>

      {/* Location Details */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          data-testid="profile-location-details-title"
        >
          Dane adresowe
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label="Adres (linia 1)"
            value={fmt(profileData.address?.addressLine1)}
            testId="profile-address1"
          />
          <InfoField
            label="Adres (linia 2)"
            value={fmt(profileData.address?.addressLine2)}
            testId="profile-address2"
          />
          <InfoField
            label="Miejscowość"
            value={fmt(profileData.address?.city)}
            testId="profile-city"
          />
          <InfoField
            label="Województwo"
            value={fmt(profileData.address?.state)}
            testId="profile-state"
          />
          <InfoField
            label="Kod pocztowy"
            value={fmt(profileData.address?.zipCode)}
            testId="profile-zip"
          />
          <InfoField
            label="Kraj"
            value={fmt(profileData.address?.country)}
            testId="profile-country"
          />
        </div>
      </div>

      {/* Signature (Technician only) */}
      {isTechnician && <SignatureSection signature={profileData.signature} />}

      {/* Tax & Compliance Details (Client only) */}
      {isClient && (
        <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
          <p
            className="text-[18px] font-medium text-on-surface-variant"
            data-testid="profile-compliance-title"
          >
            Dane umowy
          </p>

          {/* NIP */}
          <div className="flex flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="profile-nip-label"
            >
              NIP
            </h2>
            <p
              className="min-w-0 text-bodyLarge font-normal text-neutral-10"
              style={{ overflowWrap: 'anywhere' }}
              data-testid="profile-nip-value"
            >
              {nip}
            </p>
          </div>

          {/* Szczegóły umowy */}
          {hasDocs ? (
            <div className="flex flex-col gap-3 mt-4">
              <h3
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="profile-contract-details-heading"
              >
                Dane umowy
              </h3>
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                {docs.map((doc, index) => {
                  const url = doc.url?.trim();
                  const filename = doc.filename ?? '—';
                  const fileSize = doc.fileSize ?? 0;
                  const sizeLabel = formatFileSize(fileSize);

                  return (
                    <div key={index} className="flex flex-col gap-4">
                      <li className="flex items-center justify-between p-2 bg-white rounded">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="shrink-0">
                            <Tooltip
                              content="Wyświetl plik"
                              side="bottom"
                              align="center"
                              contentMaxWidth={80}
                              useContentWidth
                              forceShow
                              triggerTabIndex={-1}
                            >
                              <button
                                type="button"
                                onClick={() => url && openDocInNewTab(url)}
                                className="shrink-0 p-0 border-0 bg-transparent cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-color-30 rounded"
                                style={{ width: '40px', height: '40px' }}
                                aria-label={`Otwórz ${filename}`}
                                data-testid={`profile-doc-icon-${index}`}
                              >
                                {getDocIcon(filename)}
                              </button>
                            </Tooltip>
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <Tooltip
                              content={filename}
                              side="bottom"
                              align="center"
                              contentMaxWidth={500}
                              useContentWidth
                            >
                              <p className="text-[16px] leading-6 tracking-[0.5px] text-[#2A4455] font-normal truncate">
                                {filename}
                              </p>
                            </Tooltip>
                            <p className="text-[12px] leading-4 tracking-[0.4px] text-[#878B8E] font-normal">
                              {sizeLabel}
                            </p>
                          </div>
                        </div>
                      </li>
                    </div>
                  );
                })}
              </ul>
            </div>
          ) : !nip || nip === '—' ? (
            <div className="flex flex-col items-center justify-center py-4">
              <p
                className="text-bodyMedium text-on-surface-variant"
                data-testid="profile-compliance-empty"
              >
                Brak danych do wyświetlenia.
              </p>
            </div>
          ) : null}
        </div>
      )}

      {/* Subscription Details (Client only) */}
      {isClient && (
        <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
          <p
            className="text-[18px] font-medium text-on-surface-variant"
            data-testid="profile-subscription-title"
          >
            Szczegóły subskrypcji
          </p>

          {subscription?.planName ? (
            <div
              className="grid gap-x-8 gap-y-8"
              style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
            >
              <div className="flex min-w-0 flex-col gap-2">
                <h2
                  className="text-bodySmall font-normal text-on-surface-variant"
                  data-testid="profile-subscription-plan-label"
                >
                  Plan subskrypcji
                </h2>
                <span
                  className="inline-block w-fit max-w-full rounded-[8px] bg-information-90 px-3 py-1 text-sm font-medium text-information-30"
                  style={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    overflowWrap: 'anywhere',
                  }}
                  data-testid="profile-subscription-plan-value"
                >
                  {subscription.planName}
                </span>
              </div>
              <InfoField
                label="Status płatności"
                value={getPaymentStatusLabel(subscription.paymentStatus)}
                testId="profile-payment-status"
              />
              <InfoField
                label="Expiry date"
                value={formatDate(subscription.expiryDate)}
                testId="profile-expiry-date"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-4">
              <p
                className="text-bodyMedium text-on-surface-variant"
                data-testid="profile-subscription-empty"
              >
                Brak przypisanego planu subskrypcji.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileBasicInfo;
